import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Package, MapPin, Phone, Calendar, Truck, CheckCircle, AlertCircle } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { SUPABASE_URL, SUPABASE_ANON_KEY } from '../../config/env';

const supabaseRequest = async (endpoint, options = {}) => {
  const response = await fetch(`${SUPABASE_URL}${endpoint}`, {
    ...options,
    headers: {
      'apikey': SUPABASE_ANON_KEY,
      'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Request failed' }));
    throw new Error(error.message || 'Request failed');
  }
  return response.json();
};

const OrderDetails = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);

  const { data: orderData, isLoading } = useQuery({
    queryKey: ['orderDetails', orderId],
    queryFn: async () => {
      const data = await supabaseRequest(`/rest/v1/master_orders?order_id=eq.${orderId}&select=*&limit=1`);
      return Array.isArray(data) ? data[0] : data;
    },
    enabled: !!orderId,
  });

  useEffect(() => {
    if (orderData) {
      setOrder(orderData);
    }
  }, [orderData]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateString;
    }
  };

  const callCustomer = () => {
    const phone = String(order?.contact_number || '').replace(/\s/g, '');
    if (phone) {
      window.open(`tel:${phone}`, '_self');
    }
  };

  const openTracking = () => {
    const url = order?.tracking_url || order?.sr_last_payload?.tracking_data?.track_url;
    if (url) {
      window.open(url, '_blank');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="w-16 h-16 border-4 border-primary-500 border-t-transparent rounded-full spinner"></div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <ArrowLeft size={24} className="text-gray-700" />
          </button>
          <h1 className="text-3xl font-bold text-gray-900">Order Details</h1>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
          <AlertCircle size={48} className="mx-auto text-gray-400 mb-4" />
          <p className="text-gray-500">Order not found</p>
        </div>
      </div>
    );
  }

  const status = String(order.current_status_type || order.status || '').toUpperCase();
  const awb = order.awb_code || order.courier_tracking_number || '-';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <button
          onClick={() => navigate(-1)}
          className="p-2 hover:bg-gray-100 rounded-lg"
        >
          <ArrowLeft size={24} className="text-gray-700" />
        </button>
        <div className="flex-1">
          <div className="flex items-center justify-between">
            <h1 className="text-3xl font-bold text-gray-900">Order Details</h1>
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${
              status === 'DELIVERED' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
            }`}>
              {status}
            </span>
          </div>
        </div>
      </div>

      {/* Order Info Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <div className="flex items-center space-x-2 mb-2">
              <Package size={18} className="text-gray-500" />
              <span className="text-sm text-gray-500">Order ID</span>
            </div>
            <p className="text-lg font-semibold text-gray-900">{order.order_id || order.id}</p>
          </div>

          <div>
            <div className="flex items-center space-x-2 mb-2">
              <Truck size={18} className="text-gray-500" />
              <span className="text-sm text-gray-500">AWB Code</span>
            </div>
            <p className="text-lg font-semibold text-gray-900">{awb}</p>
          </div>

          <div>
            <div className="flex items-center space-x-2 mb-2">
              <Phone size={18} className="text-gray-500" />
              <span className="text-sm text-gray-500">Customer Contact</span>
            </div>
            <div className="flex items-center space-x-2">
              <p className="text-lg font-semibold text-gray-900">{order.contact_number || 'N/A'}</p>
              {order.contact_number && (
                <button
                  onClick={callCustomer}
                  className="p-1 text-primary-600 hover:bg-primary-50 rounded"
                >
                  <Phone size={16} />
                </button>
              )}
            </div>
          </div>

          <div>
            <div className="flex items-center space-x-2 mb-2">
              <MapPin size={18} className="text-gray-500" />
              <span className="text-sm text-gray-500">Delivery Address</span>
            </div>
            <p className="text-sm text-gray-900">{order.delivery_address || order.address || 'N/A'}</p>
          </div>

          <div>
            <div className="flex items-center space-x-2 mb-2">
              <Calendar size={18} className="text-gray-500" />
              <span className="text-sm text-gray-500">Order Date</span>
            </div>
            <p className="text-sm text-gray-900">{formatDate(order.created_at)}</p>
          </div>

          {order.amount && (
            <div>
              <div className="flex items-center space-x-2 mb-2">
                <span className="text-sm text-gray-500">Amount</span>
              </div>
              <p className="text-lg font-semibold text-gray-900">
                ₹{Number(order.amount).toLocaleString('en-IN')}
              </p>
            </div>
          )}
        </div>

        {order.tracking_url && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <button
              onClick={openTracking}
              className="w-full px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 flex items-center justify-center space-x-2"
            >
              <Truck size={18} />
              <span>Track Shipment</span>
            </button>
          </div>
        )}
      </div>

      {/* Additional Details */}
      {order.courier_name && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Shipping Details</h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-600">Courier:</span>
              <span className="font-medium text-gray-900">{order.courier_name}</span>
            </div>
            {order.delivery_type && (
              <div className="flex justify-between">
                <span className="text-gray-600">Delivery Type:</span>
                <span className="font-medium text-gray-900">{order.delivery_type}</span>
              </div>
            )}
            {order.courier_estimated_delivery && (
              <div className="flex justify-between">
                <span className="text-gray-600">Estimated Delivery:</span>
                <span className="font-medium text-gray-900">{formatDate(order.courier_estimated_delivery)}</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderDetails;

